// Database API layer for Cloudflare D1
import { API_BASE_URL, USE_MCP_SERVICE } from '../config/environment';
import mcpDatabaseService from './mcpDatabaseService';

const DATABASE_ID = 'bd73b18c-544d-4d42-9558-a6181850498e';

class DatabaseService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Route to MCP service for development or Worker API for production
  async query(sql, params = []) {
    try {
      // Use MCP service for development, Worker API for production
      if (USE_MCP_SERVICE) {
        return await mcpDatabaseService.query(sql, params);
      }

      // Production: use Cloudflare Worker API
      const response = await fetch(`${API_BASE_URL}/database/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sql, params }),
      });

      if (!response.ok) {
        throw new Error(`Database query failed: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Database query error:', error);
      // Log the error but don't fallback to mock data in production
      if (!USE_MCP_SERVICE) {
        console.error('Production API failed - no fallback available:', error);
        throw error; // Don't fall back to mock data in production
      }
      throw error;
    }
  }

  // Fetch all events with optional filtering
  async getEvents(filters = {}) {
    const cacheKey = `events_${JSON.stringify(filters)}`;
    
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      // Use dedicated events endpoint in production, SQL query for development
      if (!USE_MCP_SERVICE) {
        // Production: use optimized events endpoint
        console.log('🌐 Using production API endpoint:', API_BASE_URL);
        const params = new URLSearchParams();
        if (filters.category) params.append('category', filters.category);
        if (filters.source) params.append('source', filters.source);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);

        const apiUrl = `${API_BASE_URL}/events?${params.toString()}`;
        console.log('📡 Fetching from:', apiUrl);
        console.log('🔧 Environment details:', {
          USE_MCP_SERVICE,
          API_BASE_URL,
          currentTime: new Date().toISOString(),
          hostname: typeof window !== 'undefined' ? window.location.hostname : 'server'
        });
        
        const response = await fetch(apiUrl);
        console.log('📊 API Response status:', response.status, response.statusText);
        console.log('📊 API Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          throw new Error(`Events API failed: ${response.status} ${response.statusText}`);
        }

        // Get response text first, then try to parse as JSON
        const responseText = await response.text();
        
        // Check if we got HTML instead of JSON (common redirect/proxy issue)
        if (responseText.includes('<!doctype') || responseText.includes('<html')) {
          console.error('❌ Received HTML instead of JSON from API');
          console.error('🔧 This usually means the API endpoint is serving the wrong content');
          console.error('📝 Response preview:', responseText.substring(0, 200) + '...');
          throw new Error('API_RETURNED_HTML');
        }
        
        // Try to parse as JSON
        let result;
        try {
          result = JSON.parse(responseText);
        } catch (jsonError) {
          console.error('❌ Failed to parse API response as JSON');
          console.error('📝 Response preview:', responseText.substring(0, 200) + '...');
          throw new Error(`JSON parse failed: ${jsonError.message}`);
        }
        
        const events = result.results || [];
        console.log('✅ API returned', events.length, 'events');

        // Cache the result
        this.cache.set(cacheKey, {
          data: events,
          timestamp: Date.now(),
        });

        return events;
      }

      // Development: use SQL query through MCP service
      let sql = `
        SELECT 
          id, title, date, time, location, description, category, source, 
          source_url as url, event_type as eventType, start_date as startDate, 
          end_date as endDate, scraped_at as scrapedAt, fingerprint
        FROM events_cache 
        WHERE 1=1
      `;
      
      const params = [];
      
      if (filters.category) {
        sql += ' AND category = ?';
        params.push(filters.category);
      }
      
      if (filters.source) {
        sql += ' AND source = ?';
        params.push(filters.source);
      }
      
      if (filters.dateFrom) {
        sql += ' AND date >= ?';
        params.push(filters.dateFrom);
      }
      
      if (filters.dateTo) {
        sql += ' AND date <= ?';
        params.push(filters.dateTo);
      }

      sql += ' ORDER BY date ASC, time ASC';

      const result = await this.query(sql, params);
      const events = result.results || [];

      // Transform database format to app format
      const transformedEvents = events.map(this.transformDatabaseEvent);

      // Cache the result
      this.cache.set(cacheKey, {
        data: transformedEvents,
        timestamp: Date.now(),
      });

      return transformedEvents;
    } catch (error) {
      console.error('Failed to fetch events:', error);
      
      // Enhanced ad blocker and network error detection
      const isAdBlockerError = error.message.includes('Failed to fetch') || 
                               error.message.includes('ERR_BLOCKED_BY_CLIENT') ||
                               error.message.includes('Network request failed') ||
                               error.message.includes('TypeError: Failed to fetch') ||
                               error.name === 'TypeError';
      
      const isProxyError = error.message.includes('API_RETURNED_HTML') ||
                          error.message.includes('JSON parse failed');
      
      if (isAdBlockerError) {
        console.warn('🚫 API request failed - checking if this is an ad blocker issue');
        console.warn('🔧 The new Pages Functions proxy should prevent this...');
        
        // For production, provide specific error types for better UX
        if (!USE_MCP_SERVICE) {
          console.error('❌ Production API request failed');
          
          // Try to determine if it's really an ad blocker or network issue
          if (API_BASE_URL.startsWith('/api')) {
            console.error('💡 Using Pages Functions proxy - this should not be blocked by ad blockers');
            throw new Error('API_PROXY_FAILED');
          } else {
            console.error('💡 External API request blocked - likely by ad blocker');
            throw new Error('API_BLOCKED_BY_ADBLOCKER');
          }
        }
      }
      
      if (isProxyError) {
        console.error('❌ API proxy configuration error');
        throw new Error('API_PROXY_CONFIG_ERROR');
      }
      
      // Log error but don't fallback to mock data in production
      if (!USE_MCP_SERVICE) {
        console.error('Production events API failed - returning empty array:', error);
        return []; // Return empty array instead of fallback
      }
      return [];
    }
  }

  // Transform database event to app format
  transformDatabaseEvent(dbEvent) {
    const event = {
      id: dbEvent.id,
      title: dbEvent.title,
      date: dbEvent.date,
      time: dbEvent.time,
      location: dbEvent.location,
      description: dbEvent.description,
      category: dbEvent.category,
      source: dbEvent.source,
      url: dbEvent.url,
      eventType: dbEvent.eventType || 'single',
    };

    // Add optional fields
    if (dbEvent.startDate) event.startDate = dbEvent.startDate;
    if (dbEvent.endDate) event.endDate = dbEvent.endDate;

    // Handle different event types
    if (event.eventType === 'recurring') {
      // Parse recurrence data from description or separate fields
      // For now, we'll reconstruct basic recurrence info
      event.recurrence = this.parseRecurrenceFromEvent(event);
    }

    if (event.eventType === 'multiDay') {
      // Parse daily schedule from description or separate fields
      event.dailySchedule = this.parseDailyScheduleFromEvent(event);
    }

    return event;
  }

  // Parse recurrence information (simplified for now)
  parseRecurrenceFromEvent(event) {
    // This would be more sophisticated in a real implementation
    // For now, return basic weekly recurrence
    return {
      frequency: 'weekly',
      daysOfWeek: [new Date(event.date).toLocaleDateString('en-US', { weekday: 'long' })],
      endDate: '2025-12-31'
    };
  }

  // Parse daily schedule (simplified for now)
  parseDailyScheduleFromEvent(event) {
    if (!event.startDate || !event.endDate) return [];
    
    const schedule = [];
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      schedule.push({
        date: d.toISOString().split('T')[0],
        times: [event.time],
        focus: `Day ${schedule.length + 1}`
      });
    }
    
    return schedule;
  }

  // Insert or update events in bulk with improved batch processing
  async upsertEvents(events) {
    if (!Array.isArray(events) || events.length === 0) {
      return [];
    }

    try {
      // Use batch API endpoint in production for better performance
      if (!USE_MCP_SERVICE) {
        const response = await fetch(`${API_BASE_URL}/events/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ events }),
        });

        if (!response.ok) {
          throw new Error(`Batch upsert failed: ${response.statusText}`);
        }

        const result = await response.json();
        
        // Clear cache after updates
        this.cache.clear();
        
        return result;
      }

      // Development: process in chunks for better performance
      const chunkSize = 50; // Process 50 events at a time
      const results = [];
      
      for (let i = 0; i < events.length; i += chunkSize) {
        const chunk = events.slice(i, i + chunkSize);
        const chunkResults = await this.upsertEventChunk(chunk);
        results.push(...chunkResults);
      }

      // Clear cache after updates
      this.cache.clear();
      
      return results;
    } catch (error) {
      console.error('Failed to upsert events:', error);
      throw error;
    }
  }

  // Process a chunk of events
  async upsertEventChunk(events) {
    const sql = `
      INSERT OR REPLACE INTO events_cache 
      (id, title, date, time, location, description, category, source, source_url, 
       event_type, start_date, end_date, recurrence_data, daily_schedule_data, 
       fingerprint, scraped_at, data_freshness_score)
      VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP, ?)
    `;

    const results = [];
    for (const event of events) {
      const params = [
        event.id,
        event.title,
        event.date || event.startDate,
        event.time || '',
        event.location,
        event.description || '',
        event.category,
        event.source,
        event.url || '',
        event.eventType || 'single',
        event.startDate || null,
        event.endDate || null,
        event.recurrence ? JSON.stringify(event.recurrence) : null,
        event.dailySchedule ? JSON.stringify(event.dailySchedule) : null,
        this.createFingerprint(event),
        this.calculateFreshnessScore(event)
      ];

      const result = await this.query(sql, params);
      results.push({
        eventId: event.id,
        success: true,
        result
      });
    }

    return results;
  }

  // Calculate data freshness score based on event properties
  calculateFreshnessScore(event) {
    let score = 100; // Start with perfect score
    
    // Reduce score for missing optional fields
    if (!event.description) score -= 10;
    if (!event.time) score -= 15;
    if (!event.url) score -= 5;
    
    // Reduce score for events far in the future (might be placeholder data)
    const eventDate = new Date(event.date || event.startDate);
    const now = new Date();
    const daysFromNow = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysFromNow > 365) score -= 20; // Very far future events
    else if (daysFromNow > 180) score -= 10; // Far future events
    
    // Increase score for events with rich data
    if (event.description && event.description.length > 100) score += 5;
    if (event.eventType !== 'single') score += 5; // Complex event types
    
    return Math.max(0, Math.min(100, score));
  }

  // Create fingerprint for duplicate detection
  createFingerprint(event) {
    const date = event.date || event.startDate;
    const titleStart = event.title.substring(0, 10).toLowerCase().replace(/[^\w]/g, '');
    return `${date}-${titleStart}`;
  }

  // Get user favorites (with session fallback)
  async getFavorites(userId = null) {
    if (!userId) {
      // Return session-based favorites (handled by the app)
      return [];
    }

    try {
      const sql = 'SELECT event_id FROM favorites WHERE user_id = ?';
      const result = await this.query(sql, [userId]);
      return (result.results || []).map(row => row.event_id);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
      return [];
    }
  }

  // Add/remove favorite
  async toggleFavorite(eventId, userId = null) {
    if (!userId) {
      // Handle session-based favorites in the app
      return false;
    }

    try {
      // Check if already favorited
      const checkSql = 'SELECT id FROM favorites WHERE user_id = ? AND event_id = ?';
      const existing = await this.query(checkSql, [userId, eventId]);

      if (existing.results && existing.results.length > 0) {
        // Remove favorite
        const deleteSql = 'DELETE FROM favorites WHERE user_id = ? AND event_id = ?';
        await this.query(deleteSql, [userId, eventId]);
        return false;
      } else {
        // Add favorite
        const insertSql = 'INSERT INTO favorites (user_id, event_id) VALUES (?, ?)';
        await this.query(insertSql, [userId, eventId]);
        return true;
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      throw error;
    }
  }

  // Get distinct categories
  async getCategories() {
    try {
      // Use dedicated categories endpoint in production
      if (!USE_MCP_SERVICE) {
        const response = await fetch(`${API_BASE_URL}/categories`);
        
        if (!response.ok) {
          throw new Error(`Categories API failed: ${response.statusText}`);
        }

        const result = await response.json();
        return result.results || [];
      }

      // Development: use SQL query
      const sql = 'SELECT DISTINCT category FROM events_cache WHERE category IS NOT NULL ORDER BY category';
      const result = await this.query(sql);
      return (result.results || []).map(row => row.category);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Log error but don't fallback to mock data in production
      if (!USE_MCP_SERVICE) {
        console.error('Production categories API failed - returning empty array:', error);
        return []; // Return empty array instead of fallback
      }
      return [];
    }
  }

  // Get distinct sources
  async getSources() {
    try {
      // Use dedicated sources endpoint in production
      if (!USE_MCP_SERVICE) {
        const response = await fetch(`${API_BASE_URL}/sources`);
        
        if (!response.ok) {
          throw new Error(`Sources API failed: ${response.statusText}`);
        }

        const result = await response.json();
        return result.results || [];
      }

      // Development: use SQL query
      const sql = 'SELECT DISTINCT source FROM events_cache WHERE source IS NOT NULL ORDER BY source';
      const result = await this.query(sql);
      return (result.results || []).map(row => row.source);
    } catch (error) {
      console.error('Failed to fetch sources:', error);
      // Log error but don't fallback to mock data in production
      if (!USE_MCP_SERVICE) {
        console.error('Production sources API failed - returning empty array:', error);
        return []; // Return empty array instead of fallback
      }
      return [];
    }
  }

  // Enhanced cleanup with freshness tracking
  async cleanupStaleEvents(options = {}) {
    const {
      daysOld = 30,
      minFreshnessScore = 50,
      preserveOngoing = true,
      preserveRecurring = true,
      dryRun = false
    } = options;

    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);
      const cutoffString = cutoffDate.toISOString().split('T')[0];

      // Build cleanup query with multiple conditions
      let sql = `
        SELECT id, title, date, event_type, data_freshness_score, scraped_at
        FROM events_cache 
        WHERE (
          (date < ? AND event_type = 'single') OR
          (data_freshness_score < ? AND date < ?)
        )
      `;
      
      const params = [cutoffString, minFreshnessScore, cutoffString];

      // Preserve certain event types if requested
      if (preserveOngoing) {
        sql += ' AND event_type != "ongoing"';
      }
      
      if (preserveRecurring) {
        sql += ' AND event_type != "recurring"';
      }

      const toDelete = await this.query(sql, params);
      const eventsToDelete = toDelete.results || [];

      if (dryRun) {
        return {
          dryRun: true,
          eventsToDelete: eventsToDelete.length,
          events: eventsToDelete
        };
      }

      // Perform actual deletion
      if (eventsToDelete.length > 0) {
        const deleteIds = eventsToDelete.map(e => e.id);
        const placeholders = deleteIds.map(() => '?').join(',');
        const deleteSql = `DELETE FROM events_cache WHERE id IN (${placeholders})`;
        
        const result = await this.query(deleteSql, deleteIds);
        
        // Clear cache after cleanup
        this.cache.clear();
        
        return {
          deleted: eventsToDelete.length,
          result,
          deletedEvents: eventsToDelete
        };
      }

      return { deleted: 0, message: 'No stale events found' };
    } catch (error) {
      console.error('Failed to cleanup stale events:', error);
      throw error;
    }
  }

  // Get data quality statistics
  async getDataQualityStats() {
    try {
      const stats = await this.query(`
        SELECT 
          COUNT(*) as total_events,
          AVG(data_freshness_score) as avg_freshness,
          COUNT(CASE WHEN data_freshness_score >= 80 THEN 1 END) as high_quality,
          COUNT(CASE WHEN data_freshness_score BETWEEN 50 AND 79 THEN 1 END) as medium_quality,
          COUNT(CASE WHEN data_freshness_score < 50 THEN 1 END) as low_quality,
          COUNT(CASE WHEN description IS NULL OR description = '' THEN 1 END) as missing_description,
          COUNT(CASE WHEN time IS NULL OR time = '' THEN 1 END) as missing_time,
          COUNT(CASE WHEN source_url IS NULL OR source_url = '' THEN 1 END) as missing_url,
          MIN(scraped_at) as oldest_scrape,
          MAX(scraped_at) as newest_scrape
        FROM events_cache
      `);

      const sourceStats = await this.query(`
        SELECT 
          source,
          COUNT(*) as event_count,
          AVG(data_freshness_score) as avg_freshness,
          MIN(scraped_at) as oldest_scrape,
          MAX(scraped_at) as newest_scrape
        FROM events_cache 
        GROUP BY source 
        ORDER BY event_count DESC
      `);

      return {
        overall: stats.results?.[0] || {},
        by_source: sourceStats.results || []
      };
    } catch (error) {
      console.error('Failed to get data quality stats:', error);
      return { overall: {}, by_source: [] };
    }
  }

  // Update freshness scores for existing events
  async updateFreshnessScores(eventIds = null) {
    try {
      let sql = 'SELECT * FROM events_cache';
      let params = [];
      
      if (eventIds && Array.isArray(eventIds)) {
        const placeholders = eventIds.map(() => '?').join(',');
        sql += ` WHERE id IN (${placeholders})`;
        params = eventIds;
      }

      const events = await this.query(sql, params);
      const eventsToUpdate = events.results || [];

      const updatePromises = eventsToUpdate.map(async (dbEvent) => {
        // Convert database format to app format for scoring
        const event = this.transformDatabaseEvent(dbEvent);
        const newScore = this.calculateFreshnessScore(event);
        
        const updateSql = 'UPDATE events_cache SET data_freshness_score = ? WHERE id = ?';
        return this.query(updateSql, [newScore, event.id]);
      });

      const results = await Promise.all(updatePromises);
      
      // Clear cache after updates
      this.cache.clear();
      
      return {
        updated: results.length,
        results
      };
    } catch (error) {
      console.error('Failed to update freshness scores:', error);
      throw error;
    }
  }

  // Wrapper for backward compatibility
  async clearOldEvents(daysOld = 30) {
    return this.cleanupStaleEvents({ daysOld });
  }
}

// Export singleton instance
export const databaseService = new DatabaseService();
export default databaseService;